import React, {useEffect, useState} from 'react';
import {Button, FormHelperText, Stack, TextField, Typography} from '@mui/material';
import {useController, useFormContext} from 'react-hook-form';
import Icon from 'presentation/icons';
import {HOUSE_LABEL, OTHER_LABEL, WORK_LABEL} from './constants';
import {getInitialCustomLabel, getInitialType} from './utils';
import {translations} from 'presentation/translations-new';
import {AddressDataFormValues} from '../../../AddressForm/validation';
import {TutorialStepsId} from 'presentation/pages/private/profile/FavoriteAddresses/components/FavoriteAddressTutorials/constants';
import {AddressIdentificationProps} from './types';

const {favoriteAddressFieldPlaceholder} =
	translations['pt-br'].privateFlow.profile.changeData.address;
const {identification} = translations['pt-br'].privateFlow.profile.changeData.addresses;

export function AddressIdentification({
	restrictToCasaOnly = false,
	onCasaSelected,
}: AddressIdentificationProps) {
	const {control} = useFormContext<AddressDataFormValues>();
	const {field, fieldState} = useController({
		name: 'favoriteAddress',
		control,
	});

	const [selectedType, setSelectedType] = useState<string | undefined>(() =>
		getInitialType(field.value),
	);
	const [customLabel, setCustomLabel] = useState(() => getInitialCustomLabel(field.value));

	useEffect(() => {
		if (restrictToCasaOnly && field.value !== HOUSE_LABEL) {
			setSelectedType(HOUSE_LABEL);
			setCustomLabel('');
			field.onChange(HOUSE_LABEL);
			return;
		}

		if (customLabel) return;
		setSelectedType(getInitialType(field.value));
		setCustomLabel(getInitialCustomLabel(field.value));
	}, [field.value, restrictToCasaOnly, field]);

	const handleSelectFavorite = (type: string) => {
		if (restrictToCasaOnly && type !== HOUSE_LABEL) return;

		const previousFavoriteAddress = field.value;
		const nextFavoriteAddress = type === OTHER_LABEL ? customLabel || OTHER_LABEL : type;
		if (type !== OTHER_LABEL) {
			setCustomLabel('');
		}
		setSelectedType(type);
		field.onChange(nextFavoriteAddress);
		if (type === HOUSE_LABEL) {
			onCasaSelected?.(previousFavoriteAddress);
		}
	};

	return (
		<Stack
			id={TutorialStepsId.ADDRESS_IDENTIFICATION}
			gap={2}
			sx={{backgroundColor: 'background.paper', padding: 2, borderRadius: 1}}>
			<Stack direction="column" alignItems="flex-start" gap={2}>
				<Typography
					variant="body1"
					color="text.primary"
					fontSize="1.125rem"
					fontWeight={600}>
					{identification}
				</Typography>
				<Stack direction="row" alignItems="flex-start" gap={1}>
					<Button
						disableElevation
						startIcon={<Icon name="home-filled" size={16} />}
						size="small"
						color={selectedType === HOUSE_LABEL ? 'primary' : 'inherit'}
						onClick={() => handleSelectFavorite(HOUSE_LABEL)}>
						{HOUSE_LABEL}
					</Button>
					{!restrictToCasaOnly && (
						<>
							<Button
								disableElevation
								startIcon={<Icon name="building-outline" size={16} />}
								size="small"
								color={selectedType === WORK_LABEL ? 'primary' : 'inherit'}
								onClick={() => handleSelectFavorite(WORK_LABEL)}>
								{WORK_LABEL}
							</Button>
							<Button
								disableElevation
								startIcon={<Icon name="location-on-filled" size={16} />}
								size="small"
								color={selectedType === OTHER_LABEL ? 'primary' : 'inherit'}
								onClick={() => handleSelectFavorite(OTHER_LABEL)}>
								{OTHER_LABEL}
							</Button>
						</>
					)}
				</Stack>
				{selectedType === OTHER_LABEL && (
					<TextField
						sx={{mt: 1}}
						slotProps={{formHelperText: {sx: {fontSize: '0.75rem'}}}}
						fullWidth
						label={favoriteAddressFieldPlaceholder}
						placeholder={favoriteAddressFieldPlaceholder}
						value={customLabel}
						onChange={e => {
							setCustomLabel(e.target.value);
							field.onChange(
								e.target.value?.trim().toLocaleLowerCase() || OTHER_LABEL,
							);
						}}
					/>
				)}
				{fieldState.error ? (
					<FormHelperText error>{fieldState.error.message}</FormHelperText>
				) : null}
			</Stack>
		</Stack>
	);
}
